import { Dispatch, PureComponent, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Hotkeys from 'react-hot-keys';

import { Translate, PrintConfigItem, PrintConfig } from '../../../../api/pushVisit';
import { GO_TO_PRESCRIPTION_PAD, PPRouterProps } from '../../../../routes/paths';
import { updatePrintConfig, updateTranslate } from '../../../../store/actions/prescriptionPad';
import { getDoc } from '../../../../store/idbHandler';
import { GlobalState } from '../../../../store/reducers';
import { getPrintConfigs, getTranslate } from '../../../../store/selectors/prescriptionPad';
import { motion, AnimatePresence } from 'framer-motion';
import { PRINT_CONFIGS_KEYS_TO_LABEL } from '../../../../constants';
import { track } from '../../../../tracking';
import { Actions } from '../../../../store/actions';
import { Checkbox } from 'antd';
import { getSectionNameFromKey, isOnboardedForLab } from '../../../../pages/utils';
import { getSectionNameConfig } from '../../../../store/selectors/session';

type PrintConfigItemType = {
    pck: string;
    pci: PrintConfigItem;
    onUpdate: (pck: string, pci: PrintConfigItem) => void;
};

const PrintConfigListItem = ({ pck, pci, onUpdate }: PrintConfigItemType) => {
    const [isOn, setIsON] = useState(getCurrentPrintConfigValue(pci));
    const sectionNameConfig = useSelector(getSectionNameConfig);

    useEffect(() => {
        const newLocal = getCurrentPrintConfigValue(pci);
        setIsON(newLocal);
    }, [pci]);

    const onToggle = () => {
        if (typeof pci === 'boolean') {
            onUpdate(pck, !isOn);
        } else if (typeof pci === 'object') {
            const newObj = Object.fromEntries(Object.keys(pci).map((key) => [key, !isOn]));
            onUpdate(pck, {
                ...newObj,
                entity: !isOn,
            });
        }

        return;
    };

    const onCheckChange = (itemKey: string) => {
        if (typeof pci === 'boolean') {
            return;
        } else if (typeof pci === 'object') {
            const toggledValue = !pci[itemKey];
            onUpdate(pck, {
                ...pci,
                [itemKey]: toggledValue,
                ...(pck === 'medications' && itemKey === 'doseFrequency' && toggledValue
                    ? { frequency: true }
                    : {}),
                ...(pck === 'medications' && itemKey === 'frequency' && !toggledValue
                    ? { doseFrequency: false }
                    : {}),
            });
        }
    };

    const subOptions = Object.keys(pci).filter((k) => k !== 'entity');
    let subOptionsArray = subOptions
        .filter((itemKey) => PRINT_CONFIGS_KEYS_TO_LABEL[itemKey])
        .filter(Boolean);

    if (pck === 'labTests' && !isOnboardedForLab()) {
        subOptionsArray = subOptionsArray.filter((i) => i !== 'showLabTestsIncludedInPackage');
    }

    return (
        <div
            key={pck}
            className="bg-actionPrimary bg-opacity-10 px-12 py-8 rounded-4 flex flex-col justify-start h-full space-y-8"
        >
            <div className="flex items-center justify-between cursor-pointer" onClick={onToggle}>
                <p className={`text-13 font-600 ${isOn ? 'text-neutral-1' : 'text-neutral-5'}`}>
                    {getSectionNameFromKey(pck, sectionNameConfig) ||
                        PRINT_CONFIGS_KEYS_TO_LABEL[pck]}
                </p>
                <div className="switch">
                    <input type="checkbox" checked={isOn} />
                    <span className="slider"></span>
                </div>
            </div>
            <AnimatePresence>
                {isOn && subOptions.length > 0 && typeof pci === 'object' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="space-y-10 mt-8"
                    >
                        {subOptionsArray.map((itemKey) => {
                            return (
                                <div
                                    onClick={() => onCheckChange(itemKey)}
                                    className="flex items-center space-x-4"
                                    key={itemKey}
                                >
                                    <div className="flex flex-col items-center justify-center w-18 h-18 ">
                                        <div className="relative flex flex-col items-center justify-center w-full h-full rounded-2">
                                            <input
                                                type="checkbox"
                                                className="w-full h-full opacity-0"
                                                name="checkbox"
                                                checked={pci[itemKey]}
                                            />
                                            <div className="absolute inset-0 checkbox-fill bg-white"></div>
                                            <svg
                                                width="28"
                                                height="28"
                                                viewBox="0 0 28 28"
                                                className="absolute hidden -top-3 -left-3 fill-white cursor-pointer absolute hidden -top-3 -left-3 fill-white"
                                                preserveAspectRatio="xMidYMid meet"
                                            >
                                                <path d="M10.0547 16.8125C10.2891 17.0469 10.6875 17.0469 10.9219 16.8125L17.8125 9.92188C18.0469 9.6875 18.0469 9.28906 17.8125 9.05469L16.9688 8.21094C16.7344 7.97656 16.3594 7.97656 16.125 8.21094L10.5 13.8359L7.85156 11.2109C7.61719 10.9766 7.24219 10.9766 7.00781 11.2109L6.16406 12.0547C5.92969 12.2891 5.92969 12.6875 6.16406 12.9219L10.0547 16.8125Z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <p className="text-neutral-black text-12">
                                        {`${PRINT_CONFIGS_KEYS_TO_LABEL[itemKey]}`}
                                    </p>
                                </div>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const getCurrentPrintConfigValue = (value: PrintConfigItem): boolean => {
    if (typeof value === 'boolean') {
        return value;
    } else if (typeof value === 'object' && 'entity' in value) {
        return value.entity;
    }
    return true;
};

export const PrintConfigDialog: React.FC<{
    close: () => void;
}> = ({ close }) => {
    const [cachePrintConfigs, setCachePrintConfigs] = useState<PrintConfig>();
    const printConfigs = useSelector(getPrintConfigs);
    const dispatch = useDispatch();

    useEffect(() => {
        setCachePrintConfigs(printConfigs);
    }, [printConfigs]);

    if (!cachePrintConfigs) {
        return null;
    }

    const onUpdate = (key: string, pci: PrintConfigItem) => {
        setCachePrintConfigs({
            ...cachePrintConfigs,
            [key]: pci,
        });
    };

    const onApply = () => {
        dispatch(updatePrintConfig(cachePrintConfigs));
        close();
        return;
    };

    return (
        <Hotkeys keyName="esc" onKeyDown={close}>
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
                className="fixed py-16 bg-white rounded-8 peak-configure-pdf opacity-anim max-h-4/5 flex flex-col"
            >
                <div className="flex items-center justify-between px-16">
                    <p className="font-700 text-neutral-black">Print Settings</p>
                    <div onClick={close} className="flex items-center justify-center w-24 h-24">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 320 512"
                            className="cursor-pointer fill-neutral-5"
                        >
                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                        </svg>
                    </div>
                </div>
                <div className="flex items-baseline space-x-4 px-16">
                    <p className="mt-8 text-13">
                        Items selected below would be part of Prescription
                    </p>
                    <div className="flex items-center justify-center w-16 h-16">
                        <svg
                            viewBox="0 0 512 512"
                            width="16"
                            height="16"
                            className="fill-neutral-3"
                        >
                            <path d="M441.6 147.2H319.809c5.437-12.189 9.791-27.01 9.791-44.8 0-40.478-32.286-70.4-73.6-70.4-48.546 0-57.8 53.853-71.683 76.315-11.062 17.761-42.869 57.069-60.919 67.144C117.792 166.194 107.62 160 96 160H32c-17.673 0-32 14.327-32 32v224c0 17.673 14.327 32 32 32h64c9.167 0 17.428-3.861 23.262-10.038C153.911 445.719 190.254 480 244.364 480H267.6c59.606 0 90.256-36.084 88.631-85.87 14.3-17.108 21.279-42.155 16.635-65.494 7.229-11.856 11.348-25.395 11.885-39.582.013-.351.024-.703.033-1.054H441.6c38.337 0 70.4-31.916 70.4-70.4 0-38.161-32.77-70.4-70.4-70.4zM32 192h64v224H32V192zm409.6 64h-99.301c14.004 16.003 15.726 46.718-5.4 66.6 12.247 21.431 1.841 49.771-14.1 58.5 7.116 42.685-10.993 66.9-55.201 66.9h-23.236c-44.337 0-76.684-31.267-116.364-40.861V208.462c29.835-10.706 68.904-59.818 83.5-83.262C227.296 99.686 232 64 256 64s41.6 16 41.6 38.4c0 38.4-28.8 57.9-28.8 76.8h172.8c20.1 0 38.4 18 38.4 38.4 0 20.7-17.7 38.4-38.4 38.4zM84 384c0 11.046-8.954 20-20 20s-20-8.954-20-20 8.954-20 20-20 20 8.954 20 20z"></path>
                        </svg>
                    </div>
                </div>
                <div className="border-b border-neutral-7 mt-16"></div>
                <div className="p-16 overflow-y-scroll flex-auto">
                    <div className="flex flex-col space-y-8">
                        {Object.keys(cachePrintConfigs).map((pck) => (
                            <PrintConfigListItem
                                key={pck}
                                pck={pck}
                                pci={cachePrintConfigs[pck]}
                                onUpdate={onUpdate}
                            />
                        ))}
                    </div>
                </div>

                <div className="flex justify-between items-center px-16 pt-16 border-t border-neutral-7">
                    <p className="text-11">
                        Changes made to Print Settings would be applicable to all future
                        prescriptions.
                    </p>
                    <div>
                        <button
                            onClick={onApply}
                            className="primary-btn px-12 py-8 btn--small"
                            disabled={
                                JSON.stringify(printConfigs) === JSON.stringify(cachePrintConfigs)
                            }
                        >
                            <span className="w-full text-center whitespace-nowrap">
                                Apply changes
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </Hotkeys>
    );
};

export const LANGS: {
    label: string;
    code: 'hi' | 'en' | 'gu' | 'te' | 'mr' | 'kn' | 'pa' | 'bn' | 'ta' | 'as';
}[] = [
    {
        label: 'English',
        code: 'en',
    },
    {
        label: 'हिंदी - Hindi',
        code: 'hi',
    },
    {
        label: 'ગુજરાતી - Gujarati',
        code: 'gu',
    },
    {
        label: 'తెలుగు - Telugu',
        code: 'te',
    },
    {
        label: 'मराठी - Marathi',
        code: 'mr',
    },
    {
        label: 'ਪੰਜਾਬੀ - Punjabi',
        code: 'pa',
    },
    {
        label: 'ಕನ್ನಡ - Kannada',
        code: 'kn',
    },
    {
        label: 'বাঙালি - Bengali',
        code: 'bn',
    },
    {
        label: 'தமிழ் - Tamil',
        code: 'ta',
    },
    {
        label: 'অসমীয়া - Assamese',
        code: 'as',
    },
];

export const LANG_CODE_TO_NAME: { [k: string]: string } = {
    hi: 'हिंदी - Hindi',
    te: 'తెలుగు - Telugu',
    ta: 'தமிழ் - Tamil',
    ml: 'മലയാളം - Malayalam',
    gu: 'ગુજરાતી - Gujarati',
    bn: 'বাঙালি - Bengali',
    as: 'অসমীয়া - Assamese',
    en: 'English',
    kn: 'ಕನ್ನಡ - Kannada',
    mr: 'मराठी - Marathi',
    pa: 'ਪੰਜਾਬੀ - Punjabi',
};

type LangDialogProps = {
    dispatch: Dispatch<Actions>;
    translate: Translate;
    close: () => void;
};

type LanguageDialogState = {
    t: Translate;
};

export const getAllLang = (): { code: string; label: string }[] => {
    const doc_languages = getDoc()?.profile.professional.language;

    if (!doc_languages?.length) {
        return LANGS;
    }

    const enabledlangs = new Set(doc_languages.map((d) => d.code));

    return [
        ...doc_languages.map((d) => ({ label: LANG_CODE_TO_NAME[d.code], code: d.code })),
        ...LANGS.filter((l) => !enabledlangs.has(l.code)),
    ];
};

class LangDialog extends PureComponent<LangDialogProps, LanguageDialogState> {
    state: LanguageDialogState = {
        t: {
            ...this.props.translate,
            medications:
                this.props.translate.medications ||
                (this.props.translate.flag && this.props.translate.medications === undefined),
        },
    };

    ALL_LANG: { code: string; label: string }[] = getAllLang();

    applyChanges = (): void => {
        const { close } = this.props;
        this.props.dispatch(updateTranslate({ ...this.props.translate, ...this.state.t }));
        close();
    };

    hideLangDialog = (): void => {
        const { close } = this.props;
        close();
    };

    setT = (t: Translate) => {
        this.setState({
            t,
        });
    };

    render() {
        const { t } = this.state;
        const { translate, close } = this.props;

        return (
            <Hotkeys keyName="esc" onKeyDown={close}>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    className="language-dialog opacity-anim max-h-4/5"
                >
                    <div className="p-16 border-b border-neutral-8">
                        <div className="flex items-center justify-between">
                            <p className="font-700 text-neutral-black text-14">Translations</p>
                            <div
                                onClick={close}
                                className="flex items-center justify-center w-12 h-12 cursor-pointer"
                            >
                                <svg viewBox="0 0 320 512" className="w-16 h-16 fill-neutral-5">
                                    <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col pt-16 px-16 overflow-hidden">
                        <div className="space-y-8 overflow-auto hide-scrollbar pb-16">
                            {/* <div
                                    onClick={() => this.setT({ flag: false, lang: '' })}
                                    className="flex items-center justify-between px-12 py-10 bg-white border border-seperator-dark rounded-8 cursor-pointer"
                                >
                                    <p className="text-13 text-neutral-3 font-700">
                                        I don't want translations
                                    </p>
                                    <div>
                                        <div className="flex items-center justify-center w-24 h-24">
                                            {!t.flag ? (
                                                <svg
                                                    className="w-20 h-20 fill-actionPrimary"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    className="w-20 h-20 fill-neutral-3"
                                                >
                                                    <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200z"></path>
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div> */}

                            {this.ALL_LANG.map((l) => (
                                <div
                                    key={l.code}
                                    onClick={() => {
                                        track('TRANSLATION_LANGUAGE_SELECTED', {
                                            language: l.code,
                                        });
                                        this.setT(
                                            l.code === t.lang
                                                ? {
                                                      flag: false,
                                                      lang: '',
                                                      advices: false,
                                                      medications: false,
                                                  }
                                                : {
                                                      flag: true,
                                                      lang: l.code,
                                                      advices: true,
                                                      medications: true,
                                                  },
                                        );
                                    }}
                                    className="flex items-center justify-between px-12 py-10 bg-actionPrimary bg-opacity-10 rounded-8 cursor-pointer"
                                >
                                    <p
                                        className={`text-13 ${
                                            l.code === t.lang
                                                ? 'text-actionPrimary'
                                                : 'text-neutral-3'
                                        } font-700`}
                                    >
                                        {l.label}
                                    </p>
                                    <div>
                                        <div className="flex items-center justify-center w-24 h-24">
                                            {l.code === t.lang ? (
                                                <svg
                                                    className="w-20 h-20 fill-actionPrimary"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    className="w-20 h-20 fill-neutral-3"
                                                >
                                                    <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200z"></path>
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between items-center my-8">
                            <Checkbox
                                onChange={(e) => {
                                    track('TRANSLATION_OPTION_SELECTED', {
                                        option: 'advices',
                                        value: e.target.checked.toString(),
                                    });
                                    this.setT({
                                        ...t,
                                        flag: e.target.checked || t.flag,
                                        advices: e.target.checked,
                                    });
                                }}
                                checked={t.advices}
                            >
                                Advices
                            </Checkbox>
                            <Checkbox
                                onChange={(e) => {
                                    track('TRANSLATION_OPTION_SELECTED', {
                                        option: 'medications',
                                        value: e.target.checked.toString(),
                                    });
                                    this.setT({
                                        ...t,
                                        flag: e.target.checked || t.flag,
                                        medications: e.target.checked,
                                    });
                                }}
                                checked={t.medications}
                            >
                                Medication instructions
                            </Checkbox>
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-16 border-t border-neutral-8">
                        <p className="text-11 text-neutral-3 w-60p"></p>
                        <button
                            onClick={this.applyChanges}
                            className="primary-btn whitespace-nowrap  px-12 py-8 btn--small "
                            // disabled={t.lang === translate.lang}
                        >
                            <span className="w-full text-center">Apply Changes</span>
                        </button>
                    </div>
                </div>
            </Hotkeys>
        );
    }
}

export const LangDialogComponent = connect((state: GlobalState) => ({
    translate: getTranslate(state),
}))(LangDialog);

export const EditPrescriptionBt = () => {
    const history = useHistory();
    const params: PPRouterProps = useParams();

    const onBack = (): void => {
        if (!params.pxId) {
            history.replace('/');
        }

        history.push(GO_TO_PRESCRIPTION_PAD(params.pxId, 'edit'));
    };
    return (
        <button onClick={onBack} className="secondary-btn px-12 py-8 btn--small">
            <span className="w-full text-center">Edit Prescription</span>
        </button>
    );
};
